import {
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { Candidate, Column } from "../interface";
import {
  useGetRegistrationStep,
  useUpdateCandidateRegistrationStep,
} from "../hooks/api";
import { toast } from "react-toastify";

/**
 * Optimistic move of a card between two columns, kept until the server data reflects it.
 * Column lists are derived from their own react-query data plus these overrides, so the
 * board stays consistent with the cache (search, filters, agency switch, stale data…).
 */
export interface CandidateMove {
  candidate: Candidate;
  from: Column["id"];
  to: Column["id"];
  patch: Partial<Candidate>;
}

interface BoardContextState {
  columns: Column[];
  search: string | null;
  filters: { qualification?: string[] };
  moves: Record<Candidate["id"], CandidateMove>;
  moveCandidate: (
    candidate: Candidate,
    from: Column["id"],
    to: Column["id"]
  ) => void;
  isError?: boolean;
  isLoading?: boolean;
}

const BoardContext = createContext<BoardContextState>({
  columns: [],
  search: null,
  filters: {},
  moves: {},
  moveCandidate: () => {},
});

export const useBoard = () => useContext(BoardContext);

export const BoardProvider = ({ children, search, filters }: PropsWithChildren<{
  search: string | null;
  filters: { qualification?: string[] };
}>) => {
  const { data, isError, isLoading } = useGetRegistrationStep();
  const [moves, setMoves] = useState<Record<Candidate["id"], CandidateMove>>({});

  const mutation = useUpdateCandidateRegistrationStep({
    onError: (err) => {
      toast.error(err.response?.data?.errors?.registration_step?.[0] ?? "Une erreur est survenue");
    },
  });

  const columns: Column[] = useMemo(() => data?.data ?? [], [data]);

  const removeMove = useCallback((candidateId: Candidate["id"]) => {
    setMoves((prev) => {
      if (!prev[candidateId]) return prev;
      const { [candidateId]: _removed, ...rest } = prev;
      return rest;
    });
  }, []);

  const moveCandidate = useCallback(
    (candidate: Candidate, from: Column["id"], to: Column["id"]) => {
      if (from === to) {
        return;
      }
      // The "incomplete" column schedules a deletion 15 days later: mirror it optimistically
      const scheduledDeletionAt = new Date(new Date().setDate(new Date().getDate() + 15)).getTime();

      setMoves((prev) => ({
        ...prev,
        [candidate.id]: { candidate, from, to, patch: { scheduled_deletion_at: scheduledDeletionAt } },
      }));

      mutation
        .mutateAsync({ candidate_id: candidate.id, new_step_id: to, previous_step_id: from })
        // Both columns have been refetched by then: the override is no longer needed
        .then(() => removeMove(candidate.id))
        // Rollback on error
        .catch(() => removeMove(candidate.id));
    },
    [mutation, removeMove]
  );

  const value: BoardContextState = useMemo(
    () => ({
      columns,
      isError,
      isLoading,
      search,
      filters,
      moves,
      moveCandidate,
    }),
    [columns, isError, isLoading, search, filters, moves, moveCandidate]
  );

  return (
    <BoardContext.Provider value={value}>{children}</BoardContext.Provider>
  );
};

export const BoardConsumer = BoardContext.Consumer;
